import { CaretRightIcon } from '@phosphor-icons/react'
import { Link } from 'react-router'
import type { AntaresRunCatalogEntry } from '../../mock-data/antares-scans/index.ts'
import {
  antaresRunCatalog,
  computeRunStats,
  getAntaresRepositoryIdForSlug,
} from '../../mock-data/antares-scans/index.ts'
import { Routes } from '../../routes.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { AntaresMark } from './antares-mark.tsx'
import * as styles from './antares-scans-page.css.ts'

type Classification = 'confirmed' | 'uncertain' | 'hypothesis'

export const AntaresScansPage: React.FC = () => (
  <div className={styles.pageContainer}>
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <span className={styles.eyebrow}>
          <AntaresMark size={12} /> Antares × Pixee · cross-repo report
        </span>
        <h1 className={styles.title}>Antares scans</h1>
        <p className={styles.subtitle}>
          AI-first security detection driven by the MITRE Top 25 knowledge base, a security-specialized 1B detector, and
          threat-model-aware triage. Each row is one run — the primary number is true positives, and the composition bar
          shows how many candidates were classified as true positive, likely true positive, or false positive.
        </p>
      </div>
      <span className={styles.attributionBadge}>
        <AntaresMark size={14} /> Antares × Pixee
      </span>
    </header>

    <div className={styles.scansTable}>
      <div className={styles.tableHead} role="row">
        <span>Repository</span>
        <span>Language</span>
        <span>Scanned</span>
        <span>Composition</span>
        <span style={{ textAlign: 'right' }}>True positives</span>
        <span />
      </div>
      {antaresRunCatalog.map(entry => (
        <ScanRow key={entry.slug} entry={entry} />
      ))}
    </div>

    <AttributionFooter />
  </div>
)

type ScanRowProps = {
  entry: AntaresRunCatalogEntry
}

const ScanRow: React.FC<ScanRowProps> = ({ entry }) => {
  const stats = computeRunStats(entry.report)
  const confirmed = stats.findings.length
  const uncertain = stats.uncertain.length
  const hypothesis = stats.investigations.length
  const total = confirmed + uncertain + hypothesis
  const repositoryId = getAntaresRepositoryIdForSlug(entry.slug) ?? `antares-repo-${entry.slug}`
  const linkTarget = Routes.RepositoryDetailsPage.createPath({ repositoryId })

  return (
    <Link to={linkTarget} className={styles.scanRow} role="row">
      <div className={styles.repoCell}>
        <div className={styles.repoNameRow}>
          <span className={styles.repoName}>{entry.label}</span>
          <span className={styles.repoKindTag}>{entry.kind}</span>
        </div>
        <span className={styles.repoTargetPath}>{entry.report.target}</span>
      </div>

      <span className={styles.languageChip}>{entry.language}</span>

      <div className={styles.scannedAtCell}>
        <span className={styles.scannedAtDate}>{formatDate({ date: entry.report.started_at, type: 'mm/dd/yy' })}</span>
        <span className={styles.scannedAtTime}>
          {formatDate({ date: entry.report.started_at, type: 'time-with-zone' })}
        </span>
      </div>

      <div className={styles.compositionCell}>
        <div className={styles.compositionBarTrack} aria-label="Findings composition">
          <CompositionSegment kind="confirmed" count={confirmed} total={total} />
          <CompositionSegment kind="uncertain" count={uncertain} total={total} />
          <CompositionSegment kind="hypothesis" count={hypothesis} total={total} />
        </div>
        <div className={styles.compositionLegend}>
          <LegendItem kind="confirmed" count={confirmed} label="true positive" />
          <LegendItem kind="uncertain" count={uncertain} label="likely true positive" />
          <LegendItem kind="hypothesis" count={hypothesis} label="false positive" />
        </div>
      </div>

      <div className={styles.findingsMetricCell}>
        <span className={styles.findingsMetricValue}>{confirmed + uncertain}</span>
        <span className={styles.findingsMetricLabel}>findings</span>
      </div>

      <span className={styles.chevronCell}>
        <CaretRightIcon size={16} weight="bold" />
      </span>
    </Link>
  )
}

type CompositionSegmentProps = {
  kind: Classification
  count: number
  total: number
}

const CompositionSegment: React.FC<CompositionSegmentProps> = ({ kind, count, total }) => {
  if (count === 0 || total === 0) {
    return null
  }
  const pct = (count / total) * 100
  return <span className={styles.compositionSegment[kind]} style={{ width: `${pct}%` }} aria-hidden="true" />
}

type LegendItemProps = {
  kind: Classification
  count: number
  label: string
}

const LegendItem: React.FC<LegendItemProps> = ({ kind, count, label }) => (
  <span className={styles.legendItem}>
    <span className={styles.legendSwatch[kind]} />
    {count} {label}
  </span>
)

const AttributionFooter: React.FC = () => (
  <div className={styles.attributionFooter}>
    <AntaresMark size={22} />
    <p className={styles.attributionText}>
      Detection powered by <span className={styles.attributionStrong}>Antares</span>, a security-specialized language
      model from Cisco Foundation AI. Threat modeling, CWE planning, and finding validation use Pixee's reasoning
      models.
    </p>
  </div>
)
